import { Controller } from "@hotwired/stimulus";
import * as http from "utils/http";
import * as notifications from "utils/notifications";
import { parseErrorFromJson } from "utils/responses";

type ModalI18n = {
  amounts_positive?: string;
  create_failed?: string;
  min_greater_than_max?: string;
  mode_required?: string;
  range_amounts_required?: string;
  save_failed?: string;
  seller_id_required?: string;
  seller_name_required?: string;
  update_failed?: string;
};

export default class extends Controller {
  static targets = ["form", "nameInput", "sellerIdInput", "error", "submitButton"];
  static values = {
    mode: { type: String, default: "edit" },
    sellerId: String,
    i18n: Object,
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly nameInputTarget: HTMLInputElement;
  declare readonly hasSellerIdInputTarget: boolean;
  declare readonly sellerIdInputTarget: HTMLInputElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare modeValue: string;
  declare sellerIdValue: string;
  declare i18nValue: ModalI18n;

  cancel(): void {
    this.closeModal();
  }

  closeOnOverlayClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target === this.element || target.classList.contains("modal-overlay")) {
      this.closeModal();
    }
  }

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    this.hideError();

    const validationError = this.validate();
    if (validationError) {
      this.showError(validationError);
      return;
    }

    this.submitButtonTarget.disabled = true;

    try {
      const isCreate = this.modeValue === "create";
      const response = isCreate
        ? await http.httpPost("/sellers/manage_sellers", this.buildBody())
        : await http.httpPatch(`/sellers/manage_sellers/${this.sellerIdValue}`, this.buildBody());
      const data = await response.json();

      if (response.ok || response.status === 201) {
        notifications.success(data.message);
        this.refreshGrid();
        this.closeModal();
      } else {
        const errorMessage = data.error
          ? parseErrorFromJson(data.error)
          : data.message
            ? parseErrorFromJson(data.message)
            : this.t(isCreate ? "create_failed" : "update_failed");
        this.showError(errorMessage);
        this.submitButtonTarget.disabled = false;
      }
    } catch (_error) {
      this.showError(this.t("save_failed"));
      this.submitButtonTarget.disabled = false;
    }
  }

  private validate(): string | null {
    if (!this.nameInputTarget.value.trim()) {
      return this.t("seller_name_required");
    }

    if (this.modeValue === "create") {
      if (!this.hasSellerIdInputTarget || !this.sellerIdInputTarget.value.trim()) {
        return this.t("seller_id_required");
      }
    }

    const mode = this.selectedMode();
    if (!mode) return this.t("mode_required");

    if (mode === "WITHIN_RANGE") {
      const minInput = this.formTarget.querySelector<HTMLInputElement>(
        '[data-sellers--refund-auto-approval-target="minAmountInput"]',
      );
      const maxInput = this.formTarget.querySelector<HTMLInputElement>(
        '[data-sellers--refund-auto-approval-target="maxAmountInput"]',
      );
      const min = Number(minInput?.value);
      const max = Number(maxInput?.value);
      if (!minInput?.value || !maxInput?.value) {
        return this.t("range_amounts_required");
      }
      if (min <= 0 || max <= 0) {
        return this.t("amounts_positive");
      }
      if (min > max) {
        return this.t("min_greater_than_max");
      }
    }

    return null;
  }

  private buildBody() {
    const mode = this.selectedMode() || "DISABLED";
    const isActiveInput = this.formTarget.querySelector<HTMLInputElement>(
      '[data-sellers--refund-auto-approval-target="isActiveInput"]',
    );
    const body: {
      seller_name?: string;
      seller_id?: string;
      refund_auto_approval: {
        mode: string;
        min_amount?: string;
        max_amount?: string;
        is_active?: boolean;
      };
    } = {
      seller_name: this.nameInputTarget.value.trim() || undefined,
      refund_auto_approval: { mode },
    };

    if (this.modeValue === "create" && this.hasSellerIdInputTarget) {
      body.seller_id = this.sellerIdInputTarget.value.trim();
    }

    if (mode === "WITHIN_RANGE") {
      const minInput = this.formTarget.querySelector<HTMLInputElement>(
        '[data-sellers--refund-auto-approval-target="minAmountInput"]',
      );
      const maxInput = this.formTarget.querySelector<HTMLInputElement>(
        '[data-sellers--refund-auto-approval-target="maxAmountInput"]',
      );
      body.refund_auto_approval.min_amount = minInput?.value;
      body.refund_auto_approval.max_amount = maxInput?.value;
      body.refund_auto_approval.is_active = !!isActiveInput?.checked;
    } else if (mode === "ALWAYS") {
      body.refund_auto_approval.is_active = !!isActiveInput?.checked;
    }

    return body;
  }

  private selectedMode(): string | undefined {
    const checked = this.formTarget.querySelector<HTMLInputElement>(
      '[data-sellers--refund-auto-approval-target="modeInput"]:checked',
    );
    return checked?.value;
  }

  private refreshGrid() {
    document
      .querySelector('[data-controller="sellers--manage-sellers"]')
      ?.dispatchEvent(new CustomEvent("sellers:reload"));
  }

  private showError(message: string) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  private hideError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  private closeModal() {
    const turboFrame = document.querySelector("turbo-frame#modal");
    if (turboFrame) {
      turboFrame.removeAttribute("src");
      turboFrame.innerHTML = "";
    }
  }

  private t(key: keyof ModalI18n): string {
    return this.i18nValue?.[key] || key;
  }
}
